import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box,
  Button,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  Typography,
  Switch,
  FormControlLabel,
  Paper,
  Divider,
  InputLabel,
  Tooltip,
  IconButton,
  CircularProgress,
  InputAdornment,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Search } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../../context";
import useFetchObjects from "../../../../api/useFetchObjects";
import useAdd from "../../../../api/useAdd";
import { useNavigate } from "react-router-dom";
import { getLocalDateString } from "../../../../utils";

// Debounce function
function debounce(func, delay) {
  let timeout;
  return function (...args) {
    const context = this;
    clearTimeout(timeout);
    timeout = setTimeout(() => func.apply(context, args), delay);
  };
}

const XRayReferral = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const navigate = useNavigate();

  const today = getLocalDateString();
  const [ticketSearch, setTicketSearch] = useState("");
  const selectedTicketSnapshot = useRef(null);

  const { data: xrayTypesData } = useFetchObjects(
    ["xray-types"],
    "xray-types/",
    token
  );
  const effectiveTicketSearch = selectedTicketSnapshot.current ? "" : ticketSearch;
  const { data: ticketsData, isLoading: isLoadingTickets } = useFetchObjects(
    ["tickets-xray-reception", today, effectiveTicketSearch],
    `tickets/?visit_date=${today}&search=${encodeURIComponent(effectiveTicketSearch)}&page_size=200`,
    token,
    { enabled: !!token }
  );

  const { handleAdd, resetForm, loading } = useAdd("patient-xray", token);

  const xrayTypes = xrayTypesData || [];
  const tickets = ticketsData?.results ?? (Array.isArray(ticketsData) ? ticketsData : []);

  const [xrayDetails, setXrayDetails] = useState({
    xrayType: "",
    testDate: getLocalDateString(),
    notes: "",
    isUrgent: false,
    totalAmount: "",
    ticket: null,
    doctor: auth?.user?.user?.id,
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (resetForm) {
      setXrayDetails({
        xrayType: "",
        testDate: getLocalDateString(),
        notes: "",
        isUrgent: false,
        totalAmount: "",
        ticket: null,
        doctor: auth?.user?.user?.id,
      });
      setErrors({});
      selectedTicketSnapshot.current = null;
    }
  }, [resetForm]);

  const handleChange = (event) => {
    const { name, value, checked, type } = event.target;
    setXrayDetails((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    if (name === "xrayType") {
      const selectedType = xrayTypes.find(
        (type) => type.id.toString() === value
      );
      setXrayDetails((prev) => ({
        ...prev,
        totalAmount: selectedType ? selectedType.cost.toString() : "",
      }));
    }

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateForm = () => {
    let tempErrors = {};
    if (!xrayDetails.ticket) {
      tempErrors.ticket = t("doctor.selectTicketRequired");
    }
    if (!xrayDetails.xrayType) {
      tempErrors.xrayType = t("doctor.xrayTypeRequired");
    }
    if (!xrayDetails.testDate) {
      tempErrors.testDate = t("doctor.testDateRequired");
    }
    if (!xrayDetails.totalAmount) {
      tempErrors.totalAmount = t("doctor.totalAmountRequired");
    }
    setErrors(tempErrors);
    return Object.keys(tempErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validateForm()) {
      const patientId = typeof xrayDetails.ticket.patient === "object" ? xrayDetails.ticket.patient?.id : xrayDetails.ticket.patient;
      const payload = {
        patient: patientId,
        ticket: xrayDetails.ticket.id,
        assigned_doctor: xrayDetails.doctor,
        xray_type: xrayDetails.xrayType,
        date: xrayDetails.testDate,
        is_urgent: xrayDetails.isUrgent,
        total_amount: xrayDetails.totalAmount,
        doctor_notes: xrayDetails.notes,
      };
      handleAdd(payload);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        p: 4,
        margin: "auto",
        width: "100%",
        borderRadius: "16px",
        backgroundColor: "background.default",
        boxShadow: "0px 6px 18px rgba(0,0,0,0.12)",
      }}
    >
      {/* Go Back Icon and Title */}
      <Box display="flex" alignItems="center" mb={4}>
        <Tooltip title={t("reception.goBack")} arrow>
          <IconButton
            onClick={() => navigate(-1)}
            aria-label={t("reception.goBack")}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon size="large" />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography
        variant="h4"
        textAlign="center"
        fontWeight="bold"
        sx={{ mb: 3 }}
        color="primary"
      >
        {t("doctor.x-rayReferralForm")}
      </Typography>

      <Divider sx={{ mb: 4 }} />

      <Grid container spacing={3}>
        <Grid item xs={12} sm={6}>
          <Autocomplete
            options={tickets}
            getOptionLabel={(option) =>
              option ? `${option.ticket_number || option.id}${option.patient_name_display ? ` - ${option.patient_name_display}` : ""}` : ""
            }
            value={xrayDetails.ticket}
            inputValue={ticketSearch}
            onInputChange={(e, value) => {
              setTicketSearch(value);
              if (!value) selectedTicketSnapshot.current = null;
            }}
            onChange={(event, newValue) => {
              selectedTicketSnapshot.current = newValue ? { id: newValue.id } : null;
              setXrayDetails((prev) => ({ ...prev, ticket: newValue }));
              setErrors((prev) => ({ ...prev, ticket: "" }));
            }}
            loading={isLoadingTickets}
            renderInput={(params) => (
              <TextField
                {...params}
                label={t("print.ticketNumber")}
                placeholder={t("doctor.selectTicketPlaceholder")}
                variant="outlined"
                fullWidth
                error={!!errors.ticket}
                helperText={errors.ticket}
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <React.Fragment>
                      {isLoadingTickets ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
          />
        </Grid>
        {xrayDetails.ticket && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label={t("laboratory.patientName")}
              value={xrayDetails.ticket.patient_name_display ?? ""}
              variant="outlined"
              InputProps={{ readOnly: true }}
            />
          </Grid>
        )}

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>{t("doctor.xrayType")}</InputLabel>
            <Select
              name="xrayType"
              value={xrayDetails.xrayType}
              onChange={handleChange}
              displayEmpty
              error={!!errors.xrayType}
            >
              {xrayTypes.map((type) => (
                <MenuItem key={type.id} value={type.id.toString()}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
            {errors.xrayType && (
              <Typography color="error" variant="caption">
                {errors.xrayType}
              </Typography>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            name="testDate"
            label={t("doctor.testDate")}
            type="date"
            fullWidth
            value={xrayDetails.testDate}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            error={!!errors.testDate}
            helperText={errors.testDate}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            name="totalAmount"
            label={t("doctor.totalAmount")}
            type="number"
            fullWidth
            value={xrayDetails.totalAmount}
            onChange={handleChange}
            error={!!errors.totalAmount}
            helperText={errors.totalAmount}
          />
        </Grid>

        <Grid item xs={12}>
          <FormControlLabel
            control={
              <Switch
                checked={xrayDetails.isUrgent}
                onChange={handleChange}
                name="isUrgent"
              />
            }
            label={t("doctor.urgent")}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="notes"
            label={t("doctor.notes")}
            multiline
            rows={4}
            fullWidth
            value={xrayDetails.notes}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? (
              <CircularProgress size={24} color="inherit" />
            ) : (
              t("doctor.submit")
            )}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default XRayReferral;
